/**
 * Salary Advance / EWA — admin approval queue.
 * Staff requests via /me page, admin approves here.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Coins, CheckCircle, XCircle, Clock, AlertCircle, Loader, Banknote,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending:  'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  paid:     'bg-blue-100 text-blue-700',
};

const StatCard = ({ label, value, color, icon: Icon }) => (
  <div className={`bg-white rounded-xl p-4 border border-gray-100 border-l-4 border-l-${color}-500`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
      </div>
      {Icon && <Icon className={`w-8 h-8 text-${color}-400`} />}
    </div>
  </div>
);

const SalaryAdvanceQueuePage = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await axios.get(`${API}/salary-advances`, { params });
      setItems(data.requests || []);
      setSummary(data.summary || null);
    } catch {
      toast.error('Gagal memuat data');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (req) => {
    if (!window.confirm(`Approve advance RM${req.amount_requested.toFixed(2)} untuk ${req.staff_name}?`)) return;
    setActingId(req.id);
    try {
      await axios.post(`${API}/salary-advances/${req.id}/approve`, { note: '' });
      toast.success('Approved');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal approve');
    } finally { setActingId(''); }
  };
  const handleReject = async (req) => {
    const reason = window.prompt('Sebab reject?');
    if (!reason || !reason.trim()) return;
    setActingId(req.id);
    try {
      await axios.post(`${API}/salary-advances/${req.id}/reject`, { reason });
      toast.success('Rejected');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal reject');
    } finally { setActingId(''); }
  };
  const handleMarkPaid = async (req) => {
    if (!window.confirm(`Tandakan RM${req.amount_requested.toFixed(2)} untuk ${req.staff_name} sebagai sudah dibayar?`)) return;
    setActingId(req.id);
    try {
      await axios.post(`${API}/salary-advances/${req.id}/mark-paid`, {});
      toast.success('Marked as paid');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    } finally { setActingId(''); }
  };

  return (
    <div className="space-y-5" data-testid="salary-advance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Coins className="w-6 h-6 text-red-700" /> Advance Gaji (EWA)
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm">Permohonan staff untuk akses gaji lebih awal (max 50% net pay).</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
          {['pending', 'approved', 'rejected', 'paid', 'all'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-md ${filter === f ? 'bg-red-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {f === 'all' ? 'Semua' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard label="Pending" value={summary.pending} color="amber" icon={Clock} />
          <StatCard label="Approved" value={summary.approved} color="emerald" icon={CheckCircle} />
          <StatCard label="Paid" value={summary.paid} color="blue" icon={Banknote} />
          <StatCard label="Rejected" value={summary.rejected} color="red" icon={XCircle} />
          <StatCard label="Pending RM" value={`RM${(summary.total_pending_amount||0).toFixed(2)}`} color="red" icon={Coins} />
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : !items.length ? (
          <div className="p-12 text-center text-gray-400">
            <AlertCircle className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">Tiada permohonan dalam kategori ini.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((r) => (
              <div key={r.id} className="p-4 sm:p-5" data-testid={`adv-${r.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-gray-800 truncate">{r.staff_name}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                      <span className="text-xs text-gray-400">{r.department}</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">RM{r.amount_requested.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">
                      Net pay: RM{(r.net_pay_at_request || 0).toFixed(2)} • Max: RM{(r.max_allowed || 0).toFixed(2)} • Submitted: {(r.submitted_at || '').slice(0, 10)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Sebab: {r.reason}</p>
                    {r.rejected_reason && <p className="text-xs text-red-600 mt-1">❌ {r.rejected_reason}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.status === 'pending' && (
                      <>
                        <button onClick={() => handleApprove(r)} disabled={actingId === r.id} data-testid={`approve-${r.id}`}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Approve
                        </button>
                        <button onClick={() => handleReject(r)} disabled={actingId === r.id} data-testid={`reject-${r.id}`}
                          className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 disabled:opacity-50 flex items-center gap-1">
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </>
                    )}
                    {r.status === 'approved' && (
                      <button onClick={() => handleMarkPaid(r)} disabled={actingId === r.id} data-testid={`paid-${r.id}`}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                        <Banknote className="w-4 h-4" /> Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryAdvanceQueuePage;
